import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Tabs,
  Input,
  Button,
  Form,
  InputNumber,
  Select,
  Upload,
  Progress,
  Space,
  Popconfirm,
  message,
  Empty,
  Spin,
  Card,
  Statistic,
  Alert,
} from 'antd';
import {
  BookOutlined,
  SearchOutlined,
  PlusOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  GlobalOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard } from '../components/common';
import { useAuth } from '../context/AuthContext';
import {
  useKnowledgeSources,
  useKnowledgeSearch,
  useKnowledgeStats,
  useKnowledgeCrawlStatus,
} from '../hooks';
import { callMCPTool } from '../services/api/gateway';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Dragger } = Upload;

// AntD Tag colour per knowledge-source department. Keyed on the value stored on
// the source (the owner's `userDepartment` at creation time). Covers the current
// roles (admin/members/projects/operations) plus the legacy values still present
// on sources created before the 2026-06 role rename (sales→members). Unknown =>
// neutral 'default'. Single source of truth for the two render sites below.
const DEPARTMENT_TAG_COLORS = {
  admin: 'purple',
  members: 'blue',
  projects: 'geekblue',
  operations: 'gold',
  // legacy (pre-rename) department tags still in existing data
  sales: 'blue',
  service: 'green',
  security: 'red',
};

const departmentColor = (dept) => DEPARTMENT_TAG_COLORS[dept] || 'default';

/**
 * Safely parse MCP response data which may be JSON, a wrapped MCP content object,
 * or an error string like "MCP error ...".
 * Returns null on failure instead of throwing.
 */
const safeParseMCPResponse = (data) => {
  if (!data) return null;
  try {
    if (typeof data === 'string') return JSON.parse(data);
    if (data.content?.[0]?.text) return JSON.parse(data.content[0].text);
    return data;
  } catch {
    return null;
  }
};

// ==========================================
// Styled Components
// ==========================================

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const StatsCard = styled(Card)`
  text-align: center;
`;

const SearchResultCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SimilarityBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $score }) =>
    $score >= 0.8 ? 'var(--odin-status-completed-bg)' :
    $score >= 0.6 ? 'var(--odin-status-awaiting-bg)' :
    'var(--odin-status-failed-bg)'};
  color: ${({ $score }) =>
    $score >= 0.8 ? 'var(--odin-status-completed-text)' :
    $score >= 0.6 ? 'var(--odin-status-awaiting-text)' :
    'var(--odin-status-failed-text)'};
`;

const AddSourceForm = styled(Form)`
  max-width: 600px;
`;

const DraggerWrapper = styled.div``;

const CrawlProgressCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

// ==========================================
// Sub-components
// ==========================================

const CRAWL_PHASE_LABELS = {
  pending: 'Queued',
  crawling: 'Crawling pages…',
  processing: 'Indexing content…',
  completed: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const CrawlProgress = ({ jobId, onComplete }) => {
  const { data, isLoading } = useKnowledgeCrawlStatus(jobId);
  const doneRef = useRef(false);

  const crawlStatus = useMemo(() => {
    if (!data) return null;
    return safeParseMCPResponse(data);
  }, [data]);

  const isComplete = crawlStatus?.status === 'completed';
  const isFailed   = crawlStatus?.status === 'failed' || crawlStatus?.status === 'cancelled';
  const isDone     = isComplete || isFailed;

  // Fire onComplete once when done (complete OR failed) — never in render body
  useEffect(() => {
    if (isDone && !doneRef.current) {
      doneRef.current = true;
      // Brief delay so user sees the final state before card disappears
      const t = setTimeout(() => onComplete?.(), 2000);
      return () => clearTimeout(t);
    }
  }, [isDone, onComplete]);

  if (isLoading || !crawlStatus) {
    return (
      <CrawlProgressCard>
        <Space>
          <Spin size="small" />
          <Text style={{ color: 'var(--odin-text-primary)' }}>Checking crawl status…</Text>
        </Space>
      </CrawlProgressCard>
    );
  }

  const progress     = crawlStatus.progress ?? 0;           // 0-100 int from DB
  const pagesCrawled = crawlStatus.pages_crawled ?? 0;
  const pagesTotal   = crawlStatus.pages_total ?? 0;
  const phaseLabel   = CRAWL_PHASE_LABELS[crawlStatus.status] || crawlStatus.status;

  return (
    <CrawlProgressCard>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          {isComplete ? (
            <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
          ) : isFailed ? (
            <ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />
          ) : (
            <ClockCircleOutlined style={{ color: 'var(--odin-accent)' }} />
          )}
          <Text strong style={{ color: 'var(--odin-text-primary)' }}>
            Crawl {phaseLabel}
          </Text>
          {pagesTotal > 0 && (
            <Text style={{ color: 'var(--odin-text-secondary)' }}>
              ({pagesCrawled}/{pagesTotal} pages)
            </Text>
          )}
        </Space>
        <Progress
          percent={isComplete ? 100 : progress}
          status={isFailed ? 'exception' : isComplete ? 'success' : 'active'}
          strokeColor="var(--odin-accent)"
        />
      </Space>
    </CrawlProgressCard>
  );
};

// ── Upload progress card (mirrors CrawlProgress, used for document uploads) ─

const UPLOAD_PHASE_LABELS = {
  pending: 'Queued',
  processing: 'Processing document…',
  completed: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const UploadProgress = ({ jobId, onComplete }) => {
  const { data, isLoading } = useKnowledgeCrawlStatus(jobId);
  const doneRef = useRef(false);

  const uploadStatus = useMemo(() => {
    if (!data) return null;
    return safeParseMCPResponse(data);
  }, [data]);

  const isComplete = uploadStatus?.status === 'completed';
  const isFailed   = uploadStatus?.status === 'failed' || uploadStatus?.status === 'cancelled';
  const isDone     = isComplete || isFailed;

  useEffect(() => {
    if (isDone && !doneRef.current) {
      doneRef.current = true;
      const t = setTimeout(() => onComplete?.(), 3000);
      return () => clearTimeout(t);
    }
  }, [isDone, onComplete]);

  if (isLoading || !uploadStatus) {
    return (
      <CrawlProgressCard>
        <Space>
          <Spin size="small" />
          <Text style={{ color: 'var(--odin-text-primary)' }}>Checking upload status…</Text>
        </Space>
      </CrawlProgressCard>
    );
  }

  const progress    = uploadStatus.progress ?? 0;
  const chunks      = uploadStatus.chunks_created ?? 0;
  const phaseLabel  = UPLOAD_PHASE_LABELS[uploadStatus.status] || uploadStatus.status;

  return (
    <CrawlProgressCard>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          {isComplete ? (
            <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
          ) : isFailed ? (
            <ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />
          ) : (
            <ClockCircleOutlined style={{ color: 'var(--odin-accent)' }} />
          )}
          <Text strong style={{ color: 'var(--odin-text-primary)' }}>
            Upload {phaseLabel}
          </Text>
          {isComplete && chunks > 0 && (
            <Text style={{ color: 'var(--odin-text-secondary)' }}>
              — {chunks} chunks created
            </Text>
          )}
          {isFailed && uploadStatus.error_message && (
            <Text style={{ color: 'var(--odin-error)', fontSize: 12 }}>
              {uploadStatus.error_message}
            </Text>
          )}
        </Space>
        <Progress
          percent={isComplete ? 100 : progress}
          status={isFailed ? 'exception' : isComplete ? 'success' : 'active'}
          strokeColor="var(--odin-accent)"
        />
      </Space>
    </CrawlProgressCard>
  );
};

// ── Inline status cell for actively-crawling rows in the Sites table ────────

const CrawlStatusCell = ({ jobId, onComplete }) => {
  const { data } = useKnowledgeCrawlStatus(jobId || '');
  const crawlStatus = useMemo(
    () => (jobId ? safeParseMCPResponse(data) : null),
    [data, jobId]
  );

  const progress      = crawlStatus?.progress ?? 0;
  const pagesCrawled  = crawlStatus?.pages_crawled ?? 0;
  const pagesTotal    = crawlStatus?.pages_total ?? crawlStatus?.max_pages ?? 0;
  const chunksCreated = crawlStatus?.chunks_created ?? 0;
  const status        = crawlStatus?.status;
  const isComplete    = status === 'completed';
  const isFailed      = status === 'failed' || status === 'cancelled';

  const completedRef = useRef(false);
  useEffect(() => {
    if (isComplete && !completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [isComplete, onComplete]);

  const PHASE_LABELS = {
    pending: 'Queued',
    crawling: 'Crawling',
    processing: 'Indexing',
    completed: 'Indexed',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };

  if (!crawlStatus) {
    return (
      <Space>
        <Spin size="small" />
        <Text style={{ color: 'var(--odin-text-secondary)', fontSize: 12 }}>Crawling…</Text>
      </Space>
    );
  }

  return (
    <div style={{ minWidth: 150 }}>
      <Progress
        percent={isComplete ? 100 : progress}
        status={isFailed ? 'exception' : isComplete ? 'success' : 'active'}
        strokeColor="var(--odin-accent)"
        size="small"
        format={() => `${pagesCrawled}/${pagesTotal || '?'} pages`}
      />
      <Text style={{ fontSize: 11, color: 'var(--odin-text-tertiary)', display: 'block' }}>
        {PHASE_LABELS[status] || status}
        {chunksCreated > 0 && ` · ${chunksCreated} chunks`}
      </Text>
    </div>
  );
};

// ==========================================
// Sources Tab
// ==========================================

const SourcesTab = ({ isAdmin, userDepartment, activeJobsBySourceId = {}, onCrawlComplete, onActiveCrawlsChange, onJobsDetected }) => {
  // All users see all sources — KB reads are now general
  const deptFilter = {};
  const { data: sourcesData, isLoading, refetch } = useKnowledgeSources(
    deptFilter,
    { enabled: true }
  );

  const sources = useMemo(() => {
    if (!sourcesData) return [];
    const parsed = safeParseMCPResponse(sourcesData);
    if (!parsed) return [];
    return parsed?.sources || parsed?.data || [];
  }, [sourcesData]);

  useEffect(() => {
    if (!sources?.length) return;
    const IN_PROGRESS = ['pending', 'crawling', 'processing'];

    // Auto-hydrate in-progress jobs from DB JOIN data (survives page refresh).
    // last_job_id / last_job_status come from the LATERAL JOIN added to listSources.
    const rebuiltJobs = {};
    for (const s of sources) {
      const sourceId = s.source_id || s.id;
      if (s.last_job_id && IN_PROGRESS.includes(s.last_job_status)) {
        rebuiltJobs[sourceId] = s.last_job_id;
      }
    }
    if (Object.keys(rebuiltJobs).length > 0) {
      onJobsDetected?.(rebuiltJobs);
    }

    const count = sources.filter(s => IN_PROGRESS.includes(s.last_job_status)).length;
    onActiveCrawlsChange?.(count);
  }, [sources, onActiveCrawlsChange, onJobsDetected]);

  const handleDelete = useCallback(async (sourceId) => {
    try {
      await callMCPTool('Knowledge-MCP', 'delete_source', { source_id: sourceId });
      message.success('Source deleted successfully');
      refetch();
    } catch (err) {
      message.error('Failed to delete source');
    }
  }, [refetch]);

  const handleRefresh = useCallback(async (sourceId) => {
    try {
      await callMCPTool('Knowledge-MCP', 'refresh_source', { source_id: sourceId }, { timeout: 60000 });
      message.success('Source refresh started');
      refetch();
    } catch (err) {
      message.error('Failed to refresh source');
    }
  }, [refetch]);

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <Space>
          {record.source_type === 'url' ? (
            <GlobalOutlined style={{ color: 'var(--odin-accent)' }} />
          ) : (
            <FileTextOutlined style={{ color: 'var(--odin-success)' }} />
          )}
          <Text style={{ color: 'var(--odin-text-primary)' }}>{title || record.url || 'Untitled'}</Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'source_type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'url' ? 'blue' : 'green'}>
          {type === 'url' ? 'URL' : 'Document'}
        </Tag>
      ),
      filters: [
        { text: 'URL', value: 'url' },
        { text: 'Document', value: 'document' },
      ],
      onFilter: (value, record) => record.source_type === value,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 120,
      render: (dept) => (
        <Tag color={departmentColor(dept)}>
          {dept || 'General'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (_, record) => {
        const sourceId = record.source_id || record.id;
        const jobId = activeJobsBySourceId[sourceId];

        // Show inline crawl progress when there's an active in-session job
        if (jobId) {
          return (
            <CrawlStatusCell
              jobId={jobId}
              onComplete={() => onCrawlComplete?.(sourceId)}
            />
          );
        }

        // Static status from source record (crawl_error → red, else green/default)
        if (record.crawl_error) {
          return <Tag color="red">Error</Tag>;
        }
        if (record.last_job_status === 'completed' || record.last_crawled_at) {
          return <Tag color="green">Indexed</Tag>;
        }
        return <Tag color="default">Pending</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString() : '--',
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
    },
    ...(isAdmin ? [{
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleRefresh(record.id || record.source_id)}
            title="Refresh source"
          />
          <Popconfirm
            title="Delete this source?"
            description="All chunks will be permanently removed."
            onConfirm={() => handleDelete(record.id || record.source_id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              title="Delete source"
            />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <DataCard
      title="Sites"
      icon={DatabaseOutlined}
      loading={isLoading}
      empty={!sources.length}
      emptyText="No knowledge sources found. Add a URL or upload a document to get started."
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          style={{ color: 'var(--odin-accent)' }}
        >
          Refresh
        </Button>
      }
    >
      <StyledTable
        dataSource={sources}
        columns={columns}
        rowKey={(record) => record.id || record.source_id || Math.random().toString()}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        size="small"
      />
    </DataCard>
  );
};

// ==========================================
// Search Tab
// ==========================================

const SearchTab = ({ isAdmin, userDepartment }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  // All users search all — KB reads are now general
  const searchOptions = {};
  const { data: searchData, isLoading, isFetching } = useKnowledgeSearch(
    activeQuery,
    searchOptions,
  );

  const results = useMemo(() => {
    if (!searchData) return [];
    const parsed = safeParseMCPResponse(searchData);
    if (!parsed) return [];
    return parsed?.results || parsed?.chunks || parsed?.data || [];
  }, [searchData]);

  const handleSearch = useCallback((value) => {
    setActiveQuery(value.trim());
  }, []);

  return (
    <div>
      <Search
        placeholder="Search the knowledge base... (min 2 characters)"
        enterButton={<><SearchOutlined /> Search</>}
        size="large"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onSearch={handleSearch}
        loading={isFetching}
        style={{ marginBottom: 24 }}
      />

      {isLoading && activeQuery && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <br />
          <Text style={{ color: 'var(--odin-text-secondary)', marginTop: 16 }}>
            Searching knowledge base...
          </Text>
        </div>
      )}

      {!isLoading && activeQuery && results.length === 0 && (
        <Empty
          description={
            <Text style={{ color: 'var(--odin-text-secondary)' }}>
              No results found for "{activeQuery}"
            </Text>
          }
        />
      )}

      {results.map((result, index) => (
        <SearchResultCard key={result.id || index}>
          <Row justify="space-between" align="top">
            <Col flex="auto">
              <Text strong style={{ color: 'var(--odin-accent)', fontSize: 14 }}>
                {result.title || result.source_title || `Result ${index + 1}`}
              </Text>
              {result.url && (
                <div>
                  <Text
                    style={{ color: 'var(--odin-text-tertiary)', fontSize: 12 }}
                    ellipsis
                  >
                    {result.url}
                  </Text>
                </div>
              )}
            </Col>
            <Col>
              {(result.similarity !== undefined || result.score !== undefined) && (
                <SimilarityBadge $score={result.similarity || result.score || 0}>
                  {((result.similarity || result.score || 0) * 100).toFixed(1)}% match
                </SimilarityBadge>
              )}
            </Col>
          </Row>
          <Paragraph
            style={{ color: 'var(--odin-text-primary)', marginTop: 8, marginBottom: 0 }}
            ellipsis={{ rows: 3, expandable: true }}
          >
            {result.content || result.text || result.chunk_text || ''}
          </Paragraph>
          {result.department && (
            <Tag
              style={{ marginTop: 8 }}
              color={departmentColor(result.department)}
            >
              {result.department}
            </Tag>
          )}
        </SearchResultCard>
      ))}
    </div>
  );
};

// ==========================================
// Add Source Tab
// ==========================================

const AddSourceTab = ({ department, isAdmin, onSourceAdded, onCrawlStarted, disabled = false }) => {
  const [urlForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [activeCrawlJobId, setActiveCrawlJobId] = useState(null);
  const [activeUploadJobId, setActiveUploadJobId] = useState(null);
  const [uploadDepartment, setUploadDepartment] = useState(department || 'admin');

  const handleAddUrl = useCallback(async (values) => {
    setSubmitting(true);
    try {
      const args = {
        url: values.url,
        department: values.department || department || 'admin',
      };
      if (values.crawlDepth) args.crawl_depth = values.crawlDepth;
      if (values.maxPages) args.max_pages = values.maxPages;
      if (values.title) args.title = values.title;

      const result = await callMCPTool('Knowledge-MCP', 'add_url_source', args, { timeout: 60000 });

      // Extract job ID from response
      const parsed = safeParseMCPResponse(result);

      const jobId = parsed?.jobId || parsed?.job_id;
      const sourceId = parsed?.sourceId || parsed?.source_id;

      if (jobId) {
        setActiveCrawlJobId(jobId);
        if (sourceId) onCrawlStarted?.(sourceId, jobId);
        message.success('URL source added - crawl started');
      } else {
        message.success('URL source added successfully');
        onSourceAdded?.();
      }
      urlForm.resetFields();
    } catch (err) {
      message.error('Failed to add URL source');
    } finally {
      setSubmitting(false);
    }
  }, [department, urlForm, onSourceAdded]);

  const handleUpload = useCallback(async (info) => {
    const { file } = info;
    // Only process when the file is done (customRequest resolved)
    if (file.status !== 'done') return;

    const rawFile = file.originFileObj || file;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        try {
          // upload_document now returns immediately with job_id (async processing)
          const result = await callMCPTool('Knowledge-MCP', 'upload_document', {
            file_base64: base64,
            filename: rawFile.name,
            department: uploadDepartment,
          }, { timeout: 15000 });
          const parsed = safeParseMCPResponse(result);
          const jobId = parsed?.job_id || parsed?.jobId;
          if (jobId) {
            setActiveUploadJobId(jobId);
            message.success(`${rawFile.name} — processing started`);
          } else {
            // Fallback: no job_id means synchronous success (shouldn't happen with new backend)
            message.success(`${rawFile.name} uploaded successfully`);
            onSourceAdded?.();
          }
        } catch (err) {
          message.error(`Failed to upload ${rawFile.name}`);
        }
      };
      reader.readAsDataURL(rawFile);
    } catch (err) {
      message.error('Failed to read file');
    }
  }, [uploadDepartment, onSourceAdded]);

  const handleCrawlComplete = useCallback(() => {
    setActiveCrawlJobId(null);
    onSourceAdded?.();
  }, [onSourceAdded]);

  const handleUploadComplete = useCallback(() => {
    setActiveUploadJobId(null);
    onSourceAdded?.();
  }, [onSourceAdded]);

  return (
    <Row gutter={[24, 24]}>
      {disabled && (
        <Col span={24}>
          <Alert
            type="warning"
            showIcon
            message="Crawl in progress — please wait"
            description="A site is currently being crawled. New sources cannot be added until it completes."
            style={{
              marginBottom: 16,
            }}
          />
        </Col>
      )}

      {/* Crawl Progress */}
      {activeCrawlJobId && (
        <Col span={24}>
          <CrawlProgress jobId={activeCrawlJobId} onComplete={handleCrawlComplete} />
        </Col>
      )}

      {/* URL Form */}
      <Col xs={24} lg={12}>
        <DataCard title="Add URL Source" icon={LinkOutlined}>
          <AddSourceForm
            form={urlForm}
            layout="vertical"
            onFinish={handleAddUrl}
            style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
            initialValues={{
              department: department || 'admin',
              crawlDepth: 3,
              maxPages: 100,
            }}
          >
            <Form.Item
              name="url"
              label="URL"
              rules={[
                { required: true, message: 'Please enter a URL' },
                { type: 'url', message: 'Please enter a valid URL' },
              ]}
            >
              <Input prefix={<GlobalOutlined />} placeholder="https://docs.example.com" />
            </Form.Item>

            <Form.Item name="title" label="Display Title">
              <Input placeholder="Optional title for this source" />
            </Form.Item>

            <Form.Item name="department" label="Department">
              <Select disabled={!isAdmin}>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="sales">Sales</Select.Option>
                <Select.Option value="service">Service</Select.Option>
                <Select.Option value="security">Security</Select.Option>
              </Select>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="crawlDepth" label="Crawl Depth">
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxPages" label="Max Pages">
                  <InputNumber min={1} max={500} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={submitting}
                disabled={disabled}
                block
              >
                Add URL Source
              </Button>
            </Form.Item>
          </AddSourceForm>
        </DataCard>
      </Col>

      {/* Upload Progress */}
      {activeUploadJobId && (
        <Col span={24}>
          <UploadProgress jobId={activeUploadJobId} onComplete={handleUploadComplete} />
        </Col>
      )}

      {/* Document Upload */}
      <Col xs={24} lg={12}>
        <DataCard title="Upload Document" icon={UploadOutlined}>
          <div style={{ marginBottom: 12 }}>
            <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', marginBottom: 4 }}>
              Department
            </Text>
            <Select
              value={uploadDepartment}
              onChange={setUploadDepartment}
              disabled={!isAdmin}
              style={{ width: '100%' }}
            >
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="sales">Sales</Select.Option>
              <Select.Option value="service">Service</Select.Option>
              <Select.Option value="security">Security</Select.Option>
            </Select>
          </div>
          <DraggerWrapper>
            <Dragger
              name="file"
              multiple={false}
              accept=".pdf,.doc,.docx,.txt,.md,.csv"
              openFileDialogOnClick={true}
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => onSuccess('ok'), 0);
              }}
              onChange={handleUpload}
              showUploadList={false}
              disabled={disabled || !!activeUploadJobId}
              style={{ opacity: (disabled || activeUploadJobId) ? 0.5 : 1, cursor: (disabled || activeUploadJobId) ? 'not-allowed' : 'pointer' }}
            >
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined style={{ color: 'var(--odin-accent)', fontSize: 48 }} />
              </p>
              <p className="ant-upload-text">
                Click or drag file to upload
              </p>
              <p className="ant-upload-hint">
                Supports PDF, DOC, DOCX, TXT, MD, CSV files.
                Files are processed and chunked for semantic search.
              </p>
            </Dragger>
          </DraggerWrapper>
        </DataCard>
      </Col>
    </Row>
  );
};

// ==========================================
// Main Page Component
// ==========================================

const KnowledgePage = () => {
  const { userRoles, userDepartment, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('sources');
  const [activeJobsBySourceId, setActiveJobsBySourceId] = useState({});
  const [crawlingSourceCount, setCrawlingSourceCount] = useState(0);
  const isAnyCrawlActive =
    Object.keys(activeJobsBySourceId).length > 0 || crawlingSourceCount > 0;

  // Determine role-based permissions
  const isAdmin = hasRole('Admin');

  // Fetch stats for all departments (all users see overall stats)
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useKnowledgeStats();
  const { refetch: refetchSources } = useKnowledgeSources({});

  const handleCrawlStarted = useCallback((sourceId, jobId) => {
    setActiveJobsBySourceId(prev => ({ ...prev, [sourceId]: jobId }));
  }, []);

  // Merge in-progress jobs detected from DB (survives page refresh)
  const handleJobsDetected = useCallback((jobs) => {
    setActiveJobsBySourceId(prev => ({ ...jobs, ...prev }));
  }, []);

  const handleCrawlComplete = useCallback((sourceId) => {
    setActiveJobsBySourceId(prev => {
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
    refetchSources();
    refetchStats();
  }, [refetchSources, refetchStats]);

  const stats = useMemo(() => {
    const empty = { totalSources: 0, urlSources: 0, documentSources: 0, byDepartment: {} };
    if (!statsData) return empty;
    const parsed = safeParseMCPResponse(statsData);
    if (!parsed) return empty;
    const total = parsed?.total_sources ?? parsed?.totalSources ?? 0;
    const url = parsed?.url_sources ?? parsed?.urlSources ?? 0;
    const doc = parsed?.document_sources ?? parsed?.documentSources ?? 0;
    return {
      totalSources: total,
      urlSources: url,
      documentSources: doc,
      byDepartment: parsed?.by_department ?? parsed?.byDepartment ?? {},
    };
  }, [statsData]);

  const handleSourceAdded = useCallback(() => {
    refetchSources();
    refetchStats();
    setActiveTab('sources');
  }, [refetchSources, refetchStats]);

  const tabItems = [
    {
      key: 'sources',
      label: (
        <span>
          <DatabaseOutlined /> Sources
        </span>
      ),
      children: <SourcesTab
        isAdmin={isAdmin}
        userDepartment={userDepartment}
        activeJobsBySourceId={activeJobsBySourceId}
        onCrawlComplete={handleCrawlComplete}
        onActiveCrawlsChange={setCrawlingSourceCount}
        onJobsDetected={handleJobsDetected}
      />,
    },
    {
      key: 'search',
      label: (
        <span>
          <SearchOutlined /> Search
        </span>
      ),
      children: <SearchTab isAdmin={isAdmin} userDepartment={userDepartment} />,
    },
    {
      key: 'add',
      label: (
        <span>
          <PlusOutlined /> Add Source
        </span>
      ),
      children: <AddSourceTab
        department={userDepartment}
        isAdmin={isAdmin}
        onSourceAdded={handleSourceAdded}
        onCrawlStarted={handleCrawlStarted}
        disabled={isAnyCrawlActive}
      />,
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <BookOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Knowledge Base
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {isAdmin
            ? 'All Departments - Knowledge Base Management'
            : `${userDepartment.charAt(0).toUpperCase() + userDepartment.slice(1)} Department - Knowledge Base`}
        </Text>
      </PageHeader>

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8} lg={8}>
          <StatCard
            icon={GlobalOutlined}
            value={stats.urlSources}
            label="Total Sites"
            loading={statsLoading}
          />
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <StatCard
            icon={FileTextOutlined}
            value={stats.documentSources}
            label="Total Documents"
            color="var(--odin-success)"
            loading={statsLoading}
          />
        </Col>
        <Col xs={24} sm={8} lg={8}>
          <StatCard
            icon={DatabaseOutlined}
            value={stats.totalSources}
            label="Total Indexed"
            color="var(--odin-warning)"
            loading={statsLoading}
          />
        </Col>
      </Row>

      {/* Tabs */}
      <StyledTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </DashboardLayout>
  );
};

export default KnowledgePage;
